/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Edit2, Trash } from "react-feather";
import { RootState, useAppDispatch, useAppSelector } from "../../../store/store";
import TableHeadingTitle from "../../../shared/TableHeadingTitle/TableHeadingTitle";
import TextField from "../../../shared/Inputs/TextField/TextField";
import TextArea from "../../../shared/Inputs/TextArea/TextArea";
import { PrimaryButton } from "../../../shared/Buttons/Buttons";
import Table, { TableHeading } from "../../../shared/Table/Table";
import Modal, { ModalBody, ModalHeader } from "../../../shared/Modals/Modal";
import DeleteModal from "../../../shared/Modals/DeleteModal/DeleteModal";
import Empty from "../../../shared/Empty/Empty";
import ErrorText from "../../../shared/Inputs/ErrorText/ErrorText";
import {
  getAlumniStories,
  updateAlumniStories,
} from "../../../store/features/AdminPanel/AlumniStories/alumniStoriesApi";
import "./alumniStories.scss";

const alumniSchema = yup.object().shape({
  name: yup
    .string()
    .required("Name is required")
    .max(191, "Name must be at most 191 characters"),
  subtext: yup
    .string()
    .nullable()
    .transform((v) => (v === "" ? null : v))
    .max(500, "Subtext must be at most 500 characters"),
  description: yup.string().nullable().transform((v) => (v === "" ? null : v)),
  tags: yup
    .string()
    .nullable()
    .transform((v) => (v === "" ? null : v))
    .max(500, "Tags must be at most 500 characters"),
  badge: yup
    .string()
    .nullable()
    .transform((v) => (v === "" ? null : v))
    .max(191, "Badge must be at most 191 characters"),
  icon: yup
    .string()
    .nullable()
    .transform((v) => (v === "" ? null : v))
    .test(
      "icon-max",
      "Icon must be at most 10 characters",
      (v) => !v || Array.from(v).length <= 10
    ),
});

const gradsSchema = yup.object().shape({
  video_url: yup
    .string()
    .nullable()
    .transform((v) => (v === "" ? null : v))
    .max(1000, "URL must be at most 1000 characters"),
  name: yup
    .string()
    .nullable()
    .transform((v) => (v === "" ? null : v))
    .max(191, "Name must be at most 191 characters"),
  role: yup
    .string()
    .nullable()
    .transform((v) => (v === "" ? null : v))
    .max(191, "Role must be at most 191 characters"),
  quote: yup.string().nullable().transform((v) => (v === "" ? null : v)),
});

const EMPTY_ALUMNI = {
  name: "",
  subtext: "",
  description: "",
  tags: "",
  badge: "",
  icon: "",
};

const EMPTY_GRAD = {
  video_url: "",
  name: "",
  role: "",
  quote: "",
};

const AlumniStoriesContent = () => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((s: RootState) => s.AlumniStoriesSlice);

  const [zoomUrl, setZoomUrl] = useState("");
  const [zoomError, setZoomError] = useState("");
  const [alumni, setAlumni] = useState<any[]>([]);
  const [grads, setGrads] = useState<any[]>([]);

  // Alumni modal
  const [alumniModalOpen, setAlumniModalOpen] = useState(false);
  const [alumniEditIndex, setAlumniEditIndex] = useState<number | null>(null);

  // Grads modal
  const [gradsModalOpen, setGradsModalOpen] = useState(false);
  const [gradsEditIndex, setGradsEditIndex] = useState<number | null>(null);

  // Delete
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePending, setDeletePending] = useState<{
    type: "alumni" | "grad";
    index: number;
  } | null>(null);

  const alumniForm = useForm<any>({
    resolver: yupResolver(alumniSchema),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: EMPTY_ALUMNI,
  });
  const gradsForm = useForm<any>({
    resolver: yupResolver(gradsSchema),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: EMPTY_GRAD,
  });

  useEffect(() => {
    dispatch(getAlumniStories());
  }, [dispatch]);

  useEffect(() => {
    if (state.data) {
      setZoomUrl(state.data.zoom_url ?? "");
      setAlumni(state.data.alumni ?? []);
      setGrads(state.data.hired_grads ?? []);
    }
  }, [state.data]);

  const persist = (
    nextAlumni: any[],
    nextGrads: any[],
    nextZoom?: string
  ) => {
    dispatch(
      updateAlumniStories({
        zoom_url: nextZoom !== undefined ? nextZoom : zoomUrl,
        alumni: nextAlumni,
        hired_grads: nextGrads,
      })
    );
  };

  // ── Zoom URL ──────────────────────────────────────────────
  const saveZoom = () => {
    if (zoomUrl.length > 1000) {
      setZoomError("URL must be at most 1000 characters");
      return;
    }
    setZoomError("");
    persist(alumni, grads, zoomUrl);
  };

  // ── Alumni handlers ───────────────────────────────────────
  const openAddAlumni = () => {
    alumniForm.reset(EMPTY_ALUMNI);
    setAlumniEditIndex(null);
    setAlumniModalOpen(true);
  };

  const openEditAlumni = (index: number) => {
    alumniForm.reset(alumni[index]);
    setAlumniEditIndex(index);
    setAlumniModalOpen(true);
  };

  const closeAlumniModal = () => {
    setAlumniModalOpen(false);
    setAlumniEditIndex(null);
    alumniForm.reset(EMPTY_ALUMNI);
  };

  const onSubmitAlumni = (values: any) => {
    const next =
      alumniEditIndex !== null
        ? alumni.map((a, i) => (i === alumniEditIndex ? values : a))
        : [...alumni, values];
    setAlumni(next);
    persist(next, grads);
    closeAlumniModal();
  };

  // ── Grads handlers ────────────────────────────────────────
  const openAddGrad = () => {
    gradsForm.reset(EMPTY_GRAD);
    setGradsEditIndex(null);
    setGradsModalOpen(true);
  };

  const openEditGrad = (index: number) => {
    gradsForm.reset(grads[index]);
    setGradsEditIndex(index);
    setGradsModalOpen(true);
  };

  const closeGradsModal = () => {
    setGradsModalOpen(false);
    setGradsEditIndex(null);
    gradsForm.reset(EMPTY_GRAD);
  };

  const onSubmitGrad = (values: any) => {
    const next =
      gradsEditIndex !== null
        ? grads.map((g, i) => (i === gradsEditIndex ? values : g))
        : [...grads, values];
    setGrads(next);
    persist(alumni, next);
    closeGradsModal();
  };

  // ── Delete handler ────────────────────────────────────────
  const confirmDelete = () => {
    if (!deletePending) return;
    if (deletePending.type === "alumni") {
      const next = alumni.filter((_, i) => i !== deletePending.index);
      setAlumni(next);
      persist(next, grads);
    } else {
      const next = grads.filter((_, i) => i !== deletePending.index);
      setGrads(next);
      persist(alumni, next);
    }
    setDeleteOpen(false);
    setDeletePending(null);
  };

  return (
    <div className="alumni-stories-wrap">
      <div className="alumni-stories-top">
        <TableHeadingTitle>Alumni Stories</TableHeadingTitle>
      </div>

      {/* ── Recording / Zoom URL ──────────────────────────── */}
      <div className="as-admin-section">
        <div className="as-admin-section-head">
          <h3 className="as-admin-title">Recording / Zoom Link</h3>
        </div>
        <div className="as-zoom-row">
          <input
            className="as-zoom-input"
            type="text"
            value={zoomUrl}
            onChange={(e) => {
              setZoomUrl(e.target.value);
              if (zoomError) setZoomError("");
            }}
            placeholder="https://us06web.zoom.us/rec/share/..."
          />
          <div className="as-zoom-save">
            <PrimaryButton
              text="Save"
              type="button"
              loading={state.loading.save}
              onClick={saveZoom}
            />
          </div>
        </div>
        {zoomError && <ErrorText error={zoomError} />}
      </div>

      {/* ── Alumni Table ──────────────────────────────────── */}
      <div className="as-admin-section">
        <div className="as-admin-section-head">
          <h3 className="as-admin-title">Alumni</h3>
          <div className="button__witdh">
            <PrimaryButton
              text="Add Alumni"
              size="sm"
              type="button"
              loading={false}
              onClick={openAddAlumni}
            />
          </div>
        </div>

        <Table
          heading={
            <TableHeading>
              <th>#</th>
              <th>Name</th>
              <th>Subtext</th>
              <th>Badge</th>
              <th>Icon</th>
              <th>Tags</th>
              <th className="hide__text">.</th>
            </TableHeading>
          }
        >
          <tbody>
            {alumni.map((person: any, i: number) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={i} className="table_row_text">
                <td>{i + 1}</td>
                <td>{person.name || "—"}</td>
                <td className="as-clamp">{person.subtext || "—"}</td>
                <td>{person.badge || "—"}</td>
                <td>{person.icon || "—"}</td>
                <td className="as-clamp">{person.tags || "—"}</td>
                <td className="drag__team__action__icons">
                  <div className="action__section__action">
                    <div className="drag_test_4544">
                      <Edit2
                        className="edit__icon"
                        onClick={() => openEditAlumni(i)}
                      />
                      <Trash
                        className="delete__icon"
                        onClick={() => {
                          setDeletePending({ type: "alumni", index: i });
                          setDeleteOpen(true);
                        }}
                      />
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {alumni.length === 0 && (
          <div className="mt-5 handle__empty__container__div">
            <Empty
              emptyText="No alumni yet."
              emptyColorFulText="Add one"
              emptyText2="above."
              custombg={true}
            />
          </div>
        )}
      </div>

      {/* ── Grads Table ───────────────────────────────────── */}
      <div className="as-admin-section">
        <div className="as-admin-section-head">
          <h3 className="as-admin-title">Grads Who Got Hired</h3>
          <div className="button__witdh">
            <PrimaryButton
              text="Add Grad"
              size="sm"
              type="button"
              loading={false}
              onClick={openAddGrad}
            />
          </div>
        </div>

        <Table
          heading={
            <TableHeading>
              <th>#</th>
              <th>Name</th>
              <th>Role</th>
              <th>Video</th>
              <th className="hide__text">.</th>
            </TableHeading>
          }
        >
          <tbody>
            {grads.map((grad: any, i: number) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={i} className="table_row_text">
                <td>{i + 1}</td>
                <td>{grad.name || "—"}</td>
                <td>{grad.role || "—"}</td>
                <td>
                  {grad.video_url ? (
                    <a href={grad.video_url} target="_blank" rel="noreferrer">
                      Open
                    </a>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="drag__team__action__icons">
                  <div className="action__section__action">
                    <div className="drag_test_4544">
                      <Edit2
                        className="edit__icon"
                        onClick={() => openEditGrad(i)}
                      />
                      <Trash
                        className="delete__icon"
                        onClick={() => {
                          setDeletePending({ type: "grad", index: i });
                          setDeleteOpen(true);
                        }}
                      />
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {grads.length === 0 && (
          <div className="mt-5 handle__empty__container__div">
            <Empty
              emptyText="No grads yet."
              emptyColorFulText="Add one"
              emptyText2="above."
              custombg={true}
            />
          </div>
        )}
      </div>

      {/* ── Alumni Modal ──────────────────────────────────── */}
      <Modal
        show={alumniModalOpen}
        setShow={setAlumniModalOpen}
        onClick={closeAlumniModal}
      >
        <ModalHeader
          title={alumniEditIndex !== null ? "Edit Alumni" : "Add Alumni"}
        />
        <ModalBody>
          <form onSubmit={alumniForm.handleSubmit(onSubmitAlumni)}>
            <TextField
              type="text"
              label="Name"
              name="name"
              register={alumniForm.register}
              error={alumniForm.formState.errors?.name}
            />
            <TextField
              type="text"
              label="Subtext (e.g. 22 years old · No college degree · 2 job offers)"
              name="subtext"
              register={alumniForm.register}
              error={alumniForm.formState.errors?.subtext}
            />
            <TextArea
              label="Description"
              name="description"
              register={alumniForm.register}
              error={alumniForm.formState.errors?.description}
            />
            <TextField
              type="text"
              label="Tags (comma-separated)"
              name="tags"
              register={alumniForm.register}
              error={alumniForm.formState.errors?.tags}
              placeholder="CloudBalance Project, 2 Job Offers"
            />
            <TextField
              type="text"
              label="Badge (e.g. ⭐ Featured Story)"
              name="badge"
              register={alumniForm.register}
              error={alumniForm.formState.errors?.badge}
            />
            <TextField
              type="text"
              label="Icon (emoji)"
              name="icon"
              register={alumniForm.register}
              error={alumniForm.formState.errors?.icon}
              placeholder="🎓"
            />
            <div className="button__witdh mt-2">
              <PrimaryButton
                text={alumniEditIndex !== null ? "Update" : "Add"}
                type="submit"
                loading={state.loading.save}
              />
            </div>
          </form>
        </ModalBody>
      </Modal>

      {/* ── Grads Modal ───────────────────────────────────── */}
      <Modal
        show={gradsModalOpen}
        setShow={setGradsModalOpen}
        onClick={closeGradsModal}
      >
        <ModalHeader
          title={gradsEditIndex !== null ? "Edit Grad" : "Add Grad"}
        />
        <ModalBody>
          <form onSubmit={gradsForm.handleSubmit(onSubmitGrad)}>
            <TextField
              type="text"
              label="Name"
              name="name"
              register={gradsForm.register}
              error={gradsForm.formState.errors?.name}
            />
            <TextField
              type="text"
              label="Role / Company"
              name="role"
              register={gradsForm.register}
              error={gradsForm.formState.errors?.role}
              placeholder="Business Analyst · Acme Corp"
            />
            <TextField
              type="text"
              label="Video URL (YouTube / Wistia)"
              name="video_url"
              register={gradsForm.register}
              error={gradsForm.formState.errors?.video_url}
              placeholder="https://www.youtube.com/watch?v=..."
            />
            <TextArea
              label="Quote"
              name="quote"
              register={gradsForm.register}
              error={gradsForm.formState.errors?.quote}
            />
            <div className="button__witdh mt-2">
              <PrimaryButton
                text={gradsEditIndex !== null ? "Update" : "Add"}
                type="submit"
                loading={state.loading.save}
              />
            </div>
          </form>
        </ModalBody>
      </Modal>

      {/* ── Delete Confirmation ───────────────────────────── */}
      <DeleteModal
        showModal={deleteOpen}
        setShowModal={setDeleteOpen}
        loading={state.loading.save}
        onClick={confirmDelete}
      />
    </div>
  );
};

export default AlumniStoriesContent;
